import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import z from 'zod';

import type { CreateAsnDTO } from '@invenco/supply-interface';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/core/card';
import { Form } from '@/components/core/form';
import { TextFormField } from '@/components/forms/text-form-field';
import { OptionalWizardStep } from '@/components/wizard';
import type { WizardStepDefinition } from '@/components/wizard/wizard-context';
import { useHasFormValues } from '@/shared/hooks/use-has-form-values';
import { useIsFormValid } from '@/shared/hooks/use-is-form-valid';

const referenceSchema = z.object({
  reference: z.string().trim(),
});

const valueFields = ['reference'] as const;

export function ReferenceStep({
  step,
  onChange,
}: {
  step: WizardStepDefinition;
  onChange: (input: Partial<CreateAsnDTO>) => void;
}) {
  const form = useForm({
    resolver: zodResolver(referenceSchema),
    defaultValues: { reference: '' },
  });
  const isValid = useIsFormValid(form, referenceSchema);
  const hasValues = useHasFormValues(form, valueFields);

  const submitSection = async () => {
    let valid = false;

    await form.handleSubmit((data) => {
      onChange({ reference: data.reference || null });
      valid = true;
    })();

    return valid;
  };

  return (
    <OptionalWizardStep
      step={step}
      label="Add reference"
      complete={isValid && hasValues}
      onSubmitStep={submitSection}
    >
      <Card aria-label="Reference">
        <CardHeader>
          <CardTitle>Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <TextFormField
              control={form.control}
              name="reference"
              aria-label="Reference"
              placeholder="Reference"
              autoFocus
            />
          </Form>
        </CardContent>
      </Card>
    </OptionalWizardStep>
  );
}
